'use client'
import DeactivateUser from '@/app/(site)/[username]/components/deactivate-user'
import DerankUser from '@/app/(site)/[username]/components/derank-user'
import HireMeForm from '@/app/(site)/[username]/components/hire-me'
import AddToCollection from '@/app/components/collections/add-to-collection-panel'
import CreatePanel from '@/app/components/collections/create-collection-panel'
import EditPanel from '@/app/components/collections/edit-collection-panel'
import EditCollections from '@/app/components/collections/edit-collections-panel'
import { Modal, ModalContext, ModalType } from '@/app/components/modal'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { useLocalStorage } from 'react-use'
import GifSelector from './gif-selector'
import { StaffModeToggle } from './staff-mode-toggle'
import { Breadcrumbs } from './ui'

export default function ModalManager({
    gifId: id,
    children,
    channel,
}: {
    gifId?: string
    channel?: IChannel
    children: ReactNode
}) {
    const { user } = useContext(UserContext)
    const [modal, setModalType] = useState<ModalType>(null)
    const [modalHistory, setModalHistory] = useState<ModalType[]>([])
    useEffect(() => {
        if (modal === null) {
            /// clear history
            if (modalHistory.length > 0) {
                setModalHistory([])
            }
        } else if (modal !== modalHistory[modalHistory.length - 1]) {
            // append to history
            setModalHistory([...modalHistory, modal])
        }
    }, [modal, modalHistory])
    // get previous modal
    const previousModal = modalHistory[modalHistory.length - 2]
    const goBack = () => {
        setModalHistory(modalHistory.slice(0, -1))
        setModalType(previousModal)
    }
    // disable background scrolling when modal is open
    useEffect(() => {
        if (modal?.name) {
            document.documentElement.style.overflow = 'hidden'
        } else {
            document.documentElement.style.overflow = ''
        }
    }, [modal?.name])

    const [openCollectionCabinet, setOpenCollectionCabinet] = useLocalStorage('openCollectionCabinet', false)
    useEffect(() => {
        if (channel) {
            const canEdit = !!(user && (user.is_staff || user.username === channel.user.username))
            if (openCollectionCabinet && canEdit) {
                setModalType({
                    name: 'editCollections',
                    channelId: channel.id,
                    title: <Breadcrumbs channel={channel} />,
                })
                setOpenCollectionCabinet(false)
            }
        }
    }, [channel, openCollectionCabinet, setModalType, setOpenCollectionCabinet, user])
    return (
        <ModalContext.Provider value={{ modal, setModalType, goBack }}>
            {children}
            {modal ? (
                <Modal
                    className={cn('transition-all duration-200 ease-in-out', getClassName(modal))}
                    previousModalId={previousModal}
                    contentClassName={getContentClassName(modal)}
                    title={modal.title || getTitle(modal)}
                    goBack={goBack}
                    onClose={() => setModalType(null)}
                    titleBarComponent={getTitleBarComponent(modal)}
                >
                    {modal.name === 'addToCollection' ? (
                        <AddToCollection gifId={id} channelId={modal.channelId} key={`add-${modal.channelId}`} />
                    ) : modal.name === 'addGifsToCollection' ? (
                        <GifSelector channelId={modal.channelId} channel={modal.channel} />
                    ) : modal.name === 'createCollection' ? (
                        <CreatePanel channelId={modal.channelId?.toString()} />
                    ) : modal.name === 'editCollections' ? (
                        <EditCollections key={modal.channelId} />
                    ) : modal.name === 'deactivateUser' ? (
                        <DeactivateUser />
                    ) : modal.name === 'derankUser' ? (
                        <DerankUser />
                    ) : modal.name === 'hireMe' ? (
                        <HireMeForm />
                    ) : modal.name === 'editCollection' ? (
                        <EditPanel />
                    ) : null}
                </Modal>
            ) : null}
        </ModalContext.Provider>
    )
}

function getContentClassName(modal: ModalType) {
    switch (modal?.name) {
        case 'hireMe':
        case 'derankUser':
        case 'deactivateUser':
            return 'flex flex-1'
    }
    return ''
}

function getClassName(modal: ModalType) {
    let className = 'max-h-[1000px] max-w-desktopWidth'
    switch (modal?.name) {
        case 'createCollection':
            className = 'max-w-[512px]'
            break
        case 'addToCollection':
            className = 'max-w-[846px]'
            break
        case 'editCollection':
            className = 'max-w-[562px]'
            break
        case 'addGifsToCollection':
            className = 'w-desktopWidth max-h-[1200px] overflow-hidden'
            break
        case 'hireMe':
        case 'derankUser':
        case 'deactivateUser':
            className = 'md:w-[675px]'
            break
    }
    return className
}

function getTitleBarComponent(modal: ModalType) {
    let component: ReactNode = null
    switch (modal?.name) {
        case 'addToCollection':
        case 'editCollections':
            component = (
                <div className="pointer-events-auto absolute inset-0 -z-10 mr-8 flex flex-1 items-center justify-end">
                    <StaffModeToggle />
                </div>
            )
            break
    }
    return component
}

function getTitle(modal: ModalType) {
    let title = ''
    switch (modal?.name) {
        case 'addToCollection':
            title = 'Add To Collection'
            break
        case 'createCollection':
            title = 'Create Collection'
            break
        case 'editCollection':
            title = 'Edit Collection'
            break
        case 'addGifsToCollection':
            title = `Add GIFs to ${modal.channel?.display_name}`
            break
        case 'editCollections':
            title = 'Edit Collections'
            break
    }
    return title
}
